/**
 * NON-PRODUCTION auto-login helper.
 *
 * Purpose: let an engineer (or an automated agent driving a browser) reach the
 * authenticated app without hand-typing credentials or pressing Enter, and
 * without an existing "active session" 409 blocking the flow. Developer and
 * QA-automation convenience — it MUST NEVER activate in a production build.
 *
 * Production safety is an ALLOW-LIST of two named contexts (below): a local
 * `vite dev` server, or a deployed build that explicitly opted in. Anything
 * else — including a new, renamed, or mistyped API host — leaves this false
 * and gets the normal manual login form. The failure mode for an unrecognised
 * environment is "no bypass", never "bypass".
 *
 * HISTORY, so this is not "fixed" back and forth a third time.
 * PR #731 shipped `VITE_API_BASE_URL !== 'https://api.scrips.com'` — a DENY
 * list, correctly rejected in review because any new or mistyped prod host
 * would silently enable a bypass. The correction was `import.meta.env.DEV`
 * alone, which is safe but false in EVERY built bundle, including
 * dev-practice-v2 — removing the one use case the helper exists for, an
 * automated agent on the deployed dev site. That went unnoticed for ~3.5 weeks
 * and blocked the 54-case scheduling-AI UAT and its live-on-dev sign-off.
 *
 * The flag below is the third shape and keeps both properties: explicit
 * per-environment opt-in (visible in a CI diff, revocable without a code
 * change) with no way for production to match by accident.
 *
 * Credentials default to the shared dev test user for LOCAL convenience, and
 * are required explicitly for any deployed build — see `DEPLOYED_OPT_IN` and
 * its security note, and `VITE_DEV_LOGIN_EMAIL` / `VITE_DEV_LOGIN_PASSWORD`
 * in .env.example.
 */

/**
 * A DEPLOYED build opts in only when BOTH conditions hold: the flag is set
 * explicitly, AND credentials were supplied. The second condition matters —
 * without it, a build carrying the flag but no credentials would auto-submit
 * the shared local defaults below, silently logging in as the wrong account.
 *
 * The flag is set in `.github/workflows/dev-web.yml` and NOWHERE ELSE.
 * `prod-web.yml` does not define it, so a production build leaves this false
 * no matter what its API host is.
 *
 * SECURITY NOTE, read before adding an environment here. Vite inlines `VITE_*`
 * into the shipped bundle, so the credentials below are readable in the JS and
 * anyone who loads the site is logged in as that account. This is inherent to
 * browser auto-submit — no gate shape avoids it. Therefore the account used
 * here MUST be a dedicated, low-privilege agent account on a non-production
 * org, never a shared human account (`staff@`/`user@`): its blast radius is
 * "everyone who can reach the URL".
 */
const DEPLOYED_OPT_IN =
  import.meta.env.VITE_ENABLE_DEV_AUTO_LOGIN === 'true' &&
  !!import.meta.env.VITE_DEV_LOGIN_EMAIL &&
  !!import.meta.env.VITE_DEV_LOGIN_PASSWORD;

/**
 * Explicit local escape hatch. Set `VITE_DISABLE_DEV_AUTO_LOGIN=true` in
 * `.env.local` to get the normal login form on a local dev server.
 *
 * Why this exists: local auto-login submits the SHARED defaults below against
 * whatever API the dev server points at — usually the shared dev backend. The
 * 409 auto-takeover then evicts whoever else is signed in as that account, so a
 * developer starting `vite dev` can silently kick QA out of a UAT run. Before
 * this flag the only way to avoid that was to edit the source. Raised in review
 * of #862.
 *
 * Deliberately does NOT override `DEPLOYED_OPT_IN`: a deployed build's
 * behaviour must be decided by CI config, not by an env var a developer might
 * carry in a local file.
 */
const LOCAL_OPT_OUT = import.meta.env.VITE_DISABLE_DEV_AUTO_LOGIN === 'true';

/**
 * Could auto-login happen on this build AT ALL — before any runtime opt-out.
 *
 * Extracted so `manualLoginRequested()` can be gated on it. `?login=manual` only
 * ever SUBTRACTS from auto-login, so on a build where auto-login is impossible it
 * has nothing to subtract from and must be inert.
 *
 * NOT `import.meta.env.DEV` alone, which was the obvious fix and is wrong here:
 * dev-practice-v2 is a PRODUCTION-mode Vite build (`DEV === false`) that opts in
 * through `VITE_ENABLE_DEV_AUTO_LOGIN`. Gating on DEV would switch the escape off
 * on the one surface it exists for — the deployed build whose auto-login nobody
 * can otherwise get out of.
 */
const AUTO_LOGIN_CONFIGURED = (import.meta.env.DEV === true && !LOCAL_OPT_OUT) || DEPLOYED_OPT_IN;

/**
 * RUNTIME escape hatch: `?login=manual` on any URL suppresses auto-login for the rest of the
 * browser session, so the normal login form appears.
 *
 * WHY THIS IS NOT THE THING THE NOTE ABOVE FORBIDS. That note is right: a deployed build's
 * behaviour must be decided by CI config, not by an env var a developer carries in a local file.
 * This is a different mechanism with a different actor — a PERSON at the keyboard, asking once,
 * for their own session only. It is not a build input and it changes nothing for anyone else.
 *
 * AND IT CANNOT WEAKEN PRODUCTION, structurally rather than by care: it only ever ANDs a `false`
 * into `DEV_AUTO_LOGIN_ENABLED`. There is no value of the parameter that turns auto-login ON. A
 * production build has both gates false already, so the worst this can do there is nothing.
 *
 * WHY IT WAS NEEDED. Measured on deployed dev by Andrew, 2026-08-21: `dev-web.yml` sets the
 * deployed opt-in with the QA agent's credentials, so `dev-practice-v2` is permanently signed in
 * as `qascripsagent@yopmail.com` — an account that lacks `Manage_Clinical_Templates`. With no way
 * to reach a login form, Template Studio was unreachable on deployed dev FOR EVERYONE, including
 * Samer. The workaround was to run a local server against the dev API, which is not available to
 * anyone demoing. A convenience nobody can escape is a trap, not a convenience.
 *
 * Session-scoped on purpose: it survives navigation inside the app (so the login page, the
 * redirect and the landing page all agree) and is gone on a new tab, which keeps automated agents
 * — the reason the deployed opt-in exists — working untouched.
 */
const MANUAL_LOGIN_KEY = 'scrips.dev.manualLogin';

function manualLoginRequested(): boolean {
  // Guarded whole: this module is imported by the auth store, which is exercised in jsdom and in
  // node test environments where `window`, `location` or `sessionStorage` may be absent or throw
  // (Safari private mode throws on access, not on write). Any failure means "no escape
  // requested", which degrades to today's behaviour.
  try {
    // THE PRODUCTION GATE. Without this the constant is computed from the URL on every
    // build, and auth-store's boot path does `MANUAL_LOGIN_REQUESTED && rawAccess !== null`
    // -> authStorage.clear(). So `https://<prod>/?login=manual` would end a real visitor's
    // session — a link anyone could send. Caught by Andrew on PR #904; the split carried it
    // across from #883 unchanged because the split moved the diff and did not re-review it.
    //
    // Gated HERE rather than at the clear: the constant has two consumers (the subtractive
    // term below and auth-store's boot clear), and a gate on one of them is a gate that the
    // next consumer silently escapes.
    if (!AUTO_LOGIN_CONFIGURED) return false;
    if (typeof window === 'undefined') return false;
    const requested = new URLSearchParams(window.location.search).get('login') === 'manual';
    if (requested) window.sessionStorage.setItem(MANUAL_LOGIN_KEY, 'true');
    return window.sessionStorage.getItem(MANUAL_LOGIN_KEY) === 'true';
  } catch {
    return false;
  }
}

/** True when the person at the keyboard has asked for the login form this session. */
export const MANUAL_LOGIN_REQUESTED = manualLoginRequested();

/**
 * Enabled on a local Vite dev server (unless opted out), or on a deployed build
 * that explicitly opted in (dev-practice-v2). False everywhere else — the
 * normal manual login form is shown and an active-session 409 keeps its
 * confirmation dialog.
 *
 * A person can always opt OUT for their own session with `?login=manual`, which is the only way
 * to reach a login form on a deployed build that opted in. That term is subtractive only and
 * cannot enable anything.
 */
export const DEV_AUTO_LOGIN_ENABLED =
  AUTO_LOGIN_CONFIGURED &&
  // Last, and only ever subtractive — see MANUAL_LOGIN_REQUESTED.
  !MANUAL_LOGIN_REQUESTED;

/** Default dev test-user credentials (overridable via env). */
export const DEV_AUTO_LOGIN_CREDENTIALS = {
  email: import.meta.env.VITE_DEV_LOGIN_EMAIL ?? 'user@scrips.com',
  password: import.meta.env.VITE_DEV_LOGIN_PASSWORD ?? 'Pa$$word123',
} as const;
